import { Component, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';


@Component({
  selector: 'app-custom-webdevelopment',
  imports: [CommonModule],
  standalone: true,
  templateUrl: './custom-webdevelopment.component.html',
  styleUrl: './custom-webdevelopment.component.scss'
})
export class CustomWebdevelopmentComponent implements OnInit, OnDestroy {
  private jsonLdScript: any;
  constructor(private router: Router, private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }
  nav(url: any) {
    console.log(url);
    this.router.navigateByUrl(url)
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route: { data: any; }) => route.data)
    )
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  injectSchemaMarkup(): void {
    const caseStudySchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/custom-web-development",
          "url": "https://www.kalpitatechnologies.com/kalpita-case-studies/custom-web-development",
          "name": "End-to-End Custom Website Development for Cloud-Based Company – Case Study",
          "description": "Case study on how Kalpita Technologies designed and developed a custom end-to-end website for a cloud service provider specializing in cloud consulting, monitoring, and security, delivering a responsive, SEO-friendly website hosted on Azure in just 3 weeks with over 80% performance scores.",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [
              ".saas-heading",
              ".saas-para",
              ".challenge-head"
            ]
          },
          "datePublished": "2025-01-28",
          "dateModified": "2025-01-28",
          "isPartOf": {
            "@type": "WebSite",
            "@id": "https://www.kalpitatechnologies.com/#website",
            "name": "Kalpita Technologies",
            "url": "https://www.kalpitatechnologies.com/"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/custom-web-development#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "item": {
                "@type": "WebPage",
                "@id": "https://www.kalpitatechnologies.com/",
                "url": "https://www.kalpitatechnologies.com/",
                "name": "Home"
              }
            },
            {
              "@type": "ListItem",
              "position": 2,
              "item": {
                "@type": "WebPage",
                "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/case-study",
                "url": "https://www.kalpitatechnologies.com/kalpita-case-studies/case-study",
                "name": "Case Studies"
              }
            },
            {
              "@type": "ListItem",
              "position": 3,
              "item": {
                "@type": "WebPage",
                "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/custom-web-development",
                "url": "https://www.kalpitatechnologies.com/kalpita-case-studies/custom-web-development",
                "name": "Custom Website Development for Cloud Company"
              }
            }
          ]
        },
        {
          "@type": "Article",
          "headline": "End-to-End Custom Website Development for Cloud-Based Company",
          "alternativeHeadline": "Ailesol Case Study – Responsive, SEO-Friendly Website Delivered in 3 Weeks on Azure for a Cloud Consulting Provider",
          "articleBody": "Kalpita Technologies designed and developed an end-to-end custom website for a cloud service provider excelling in cloud consulting solutions involving cloud monitoring and security services...",
          "keywords": [
            "custom website development", "cloud company website", "end-to-end web development", "responsive website design", "SEO-friendly website", "Azure website hosting", "cloud services website", "Angular web development", "ASP.NET website", "custom web design case study"
          ],
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "datePublished": "2025-01-28",
          "image": "https://www.kalpitatechnologies.com/assets/resources/casestudies/web-development/web-dev.webp",
          "articleSection": "Case Studies"
        },
        {
          "@type": "ItemList",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/custom-web-development#sections",
          "name": "Key Sections",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "Article", "name": "Objective", "url": "https://www.kalpitatechnologies.com/kalpita-case-studies/custom-web-development#objective" } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "Article", "name": "The Challenge", "url": "https://www.kalpitatechnologies.com/kalpita-case-studies/custom-web-development#challenge" } }
          ]
        },
        {
          "@type": "HowTo",
          "name": "How to Deliver an End-to-End Custom Website for a Cloud Company in 3 Weeks",
          "description": "Step-by-step approach Kalpita Technologies used to design, develop, test, and deploy a custom website.",
          "step": [
            { "@type": "HowToStep", "name": "Create Responsive Wireframes", "text": "Design wireframes with responsive user interface and experience." },
            { "@type": "HowToStep", "name": "Plan Architecture", "text": "Plan service-based file structures and website architecture." },
            { "@type": "HowToStep", "name": "Develop and Test", "text": "Build using .NET Core Angular and test across browsers for performance." },
            { "@type": "HowToStep", "name": "Deploy", "text": "Host on Azure Cloud using DevOps CI/CD pipelines." }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "How quickly was the website delivered?", "acceptedAnswer": { "@type": "Answer", "text": "The end-to-end website was completed and deployed to Azure in up to 3 weeks." } },
            { "@type": "Question", "name": "What technologies were used?", "acceptedAnswer": { "@type": "Answer", "text": "The stack includes .NET Core, Angular, ASP.NET, Azure, and Azure DevOps." } }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png"
          }
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://www.kalpitatechnologies.com/#localbusiness",
          "name": "Kalpita Technologies — Austin Office",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
            "addressLocality": "Austin", "addressRegion": "TX", "postalCode": "78746", "addressCountry": "US"
          },
          "telephone": "+1-512-240-2484",
          "url": "https://www.kalpitatechnologies.com/"
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(caseStudySchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
}
